/**
 * The Case IDE's chat: `useAgentChat` for the case scope with adapters wired
 * to the IDE (editor selection, artifact opening, cache invalidation).
 */
import { useMemo, useRef } from "react";

import {
  useAgentChat,
  type ChatAdapters,
  type Me,
  type SerializedSelection
} from "@/features/agents/useAgentChat";

import {
  describeOpenDraft,
  runClientTool
} from "@/features/draftEditor/agentRelay";

import { describeOpenFiles } from "@/features/officeEditor/openFiles";

import { useCaseIde } from "../context/CaseIdeContext";
import type { OpenArtifactData } from "../types";

export {
  ASK_USER_PART,
  lastAssistantMessageIsCompleteWithAnswers,
  parseTurnInProgress
} from "@/features/agents/useAgentChat";
export type {
  AgentMessageMetadata as CaseAgentMessageMetadata,
  AgentUIMessage as CaseAgentUIMessage,
  Me,
  MentionRef,
  MessageAuthor
} from "@/features/agents/useAgentChat";

type Options = {
  /** Resolve a title for an artifact the agent asks to open. */
  titleFor: (artifact: OpenArtifactData) => string;
  /**
   * Text pinned by the panel after the editor's live selection collapsed
   * (focus moved to the prompt). Wins over the live selection when set.
   */
  pinnedText?: () => string | null;
  /** Who is typing; stamped on outgoing messages so teammates see a name. */
  me?: Me | null;
  /** A thread to open on mount (deep link), overriding the remembered one. */
  initialThreadId?: string | null;
};

const serializeSelection = (
  s: ReturnType<typeof useCaseIde>["selection"],
  pinned: string | null
): SerializedSelection => ({
  artifact: s.artifact
    ? {
        kind: s.artifact.kind,
        id: s.artifact.id,
        title: s.artifact.title,
        page: s.artifact.page ?? null
      }
    : null,
  text: pinned ?? s.text,
  selected_node_ids: s.selectedNodeIds,
  // Drafts only: the docx tools are draft-bound. A Word case file open in the
  // editor is reported through open_files instead.
  open_draft: describeOpenDraft(
    s.artifact?.kind === "draft" ? s.artifact.id : null
  ),
  open_files: describeOpenFiles()
});

export const useCaseAgentChat = ({
  titleFor,
  pinnedText,
  me,
  initialThreadId
}: Options) => {
  const { individualId, selection, invalidate, open } = useCaseIde();

  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const titleForRef = useRef(titleFor);
  titleForRef.current = titleFor;
  const pinnedTextRef = useRef(pinnedText);
  pinnedTextRef.current = pinnedText;
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;
  const openRef = useRef(open);
  openRef.current = open;

  const adapters = useMemo<ChatAdapters>(
    () => ({
      selection: () =>
        serializeSelection(
          selectionRef.current,
          pinnedTextRef.current?.() ?? null
        ),
      onInvalidate: (targets) => invalidateRef.current(targets),
      titleFor: (a) => titleForRef.current(a),
      onOpenArtifact: (a) =>
        openRef.current({
          kind: a.kind,
          id: a.id,
          title: a.title ?? titleForRef.current(a),
          page: a.page ?? null
        }),
      onClientTool: (request) => runClientTool(request)
    }),
    []
  );

  const scope = useMemo(
    () => ({ kind: "case" as const, id: individualId }),
    [individualId]
  );
  return useAgentChat({ scope, me, initialThreadId, adapters });
};
